import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/slices/common";
import { updateDance } from "../../http/dance";
import TimePresetButtons from "../../components/FormElements/TimePresetButtons";

const AvailabilityModal = ({ open, onClose, dance, onSuccess }) => {
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isUnscheduled, setIsUnscheduled] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (dance) {
      setStartTime(
        dance.availabilityStartTime
          ? dayjs(dance.availabilityStartTime)
          : null
      );
      setEndTime(
        dance.availabilityEndTime ? dayjs(dance.availabilityEndTime) : null
      );
      setIsUnscheduled(Boolean(dance.isUnscheduled));
      setError("");
    }
  }, [dance]);

  const handleSave = async () => {
    // Validate that end time is after start time if both are provided
    if (startTime && endTime && endTime.isBefore(startTime)) {
      setError("End time must be after start time");
      return;
    }

    try {
      dispatch(setLoading(true));
      setError("");

      const payload = { isUnscheduled };

      // Include start time if set, or explicitly null to clear it
      if (startTime) {
        payload.availabilityStartTime = startTime.toISOString();
      } else if (dance.availabilityStartTime && !startTime) {
        // If there was a value and now it's cleared, send null
        payload.availabilityStartTime = null;
      }

      // Include end time if set, or explicitly null to clear it
      if (endTime) {
        payload.availabilityEndTime = endTime.toISOString();
      } else if (dance.availabilityEndTime && !endTime) {
        // If there was a value and now it's cleared, send null
        payload.availabilityEndTime = null;
      }

      await updateDance(payload, dance._id);
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating availability:", err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to update"
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleClear = () => {
    setStartTime(null);
    setEndTime(null);
    setError("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="#142C5C">
            Update Availability
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dance?.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUnscheduled}
                  onChange={(e) => {
                    setIsUnscheduled(e.target.checked);
                    if (e.target.checked) {
                      setStartTime(null);
                      setEndTime(null);
                    }
                    setError("");
                  }}
                />
              }
              label={
                <span style={{ fontSize: "14px" }}>
                  Release date not decided yet (TBD)
                  <span
                    style={{ fontSize: "11px", color: "gray", display: "block" }}
                  >
                    Track stays hidden from users until it's scheduled
                  </span>
                </span>
              }
            />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "#142C5C", fontWeight: 500 }}
              >
                Start Date & Time
              </Typography>
              <DateTimePicker
                value={startTime}
                disabled={isUnscheduled}
                onChange={(newValue) => {
                  setStartTime(newValue);
                  setError("");
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "Select start date and time",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#142C5C",
                        },
                        "&:hover fieldset": {
                          borderColor: "#142C5C",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#142C5C",
                        },
                      },
                    },
                  },
                }}
              />
              {!isUnscheduled && (
                <TimePresetButtons value={startTime} onChange={(val) => { setStartTime(val); setError(""); }} />
              )}
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "#142C5C", fontWeight: 500 }}
              >
                End Date & Time
              </Typography>
              <DateTimePicker
                value={endTime}
                disabled={isUnscheduled}
                onChange={(newValue) => {
                  setEndTime(newValue);
                  setError("");
                }}
                minDateTime={startTime || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "Select end date and time",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#142C5C",
                        },
                        "&:hover fieldset": {
                          borderColor: "#142C5C",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#142C5C",
                        },
                      },
                    },
                  },
                }}
              />
              {!isUnscheduled && (
                <TimePresetButtons value={endTime} onChange={(val) => { setEndTime(val); setError(""); }} />
              )}
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              Leave fields empty to make the track always available, or check
              "Release date not decided yet" to keep it hidden until scheduled.
            </Typography>
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={handleClear}
          variant="outlined"
          sx={{
            borderColor: "#666",
            color: "#666",
            borderRadius: "6px",
            "&:hover": {
              borderColor: "#333",
              backgroundColor: "transparent",
            },
          }}
        >
          Clear
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#142C5C",
            color: "#142C5C",
            borderRadius: "6px",
            "&:hover": {
              borderColor: "#0f2247",
              backgroundColor: "transparent",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: "#142C5C",
            color: "#fff",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#0f2247",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilityModal;
